import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import InfoPopoverButton from '../../components/InfoPopoverButton';
import { toast } from 'sonner';
import axios from 'axios';
import AdminBottomNav from '../../components/AdminBottomNav';
import {
  VenueStaffMemberCard,
  VenueStaffPendingInviteCard,
} from '../../components/venue/VenueStaffComponents';
import { shareOrCopy } from '../../utils/shareOrCopy';
import { copyToClipboard } from '../../utils/copyToClipboard';
import VenueEmptyState from '../../components/venue/VenueEmptyState';
import StaffVenueHeader from '../../components/staff/StaffVenueHeader';
import { trackInviteCreated, trackInviteShared } from '../../utils/airbridge';
import { buildShareUrl } from '../../utils/shareBaseUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const buildInviteUrl = (token) => buildShareUrl(`/staff-invite/${token}`);

export default function AdminMembersPage() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const currentUserId = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').id; } catch { return null; }
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    const activeVenueAppId = localStorage.getItem('activeVenueAppId');
    const headers = { Authorization: `Bearer ${token}` };
    if (activeVenueAppId) headers['X-Active-Venue-App-Id'] = activeVenueAppId;
    return headers;
  };

  const fetchMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/venue/staff`, { headers: getHeaders() });
      const next = Array.isArray(res.data) ? res.data : [];
      setMembers(next);
      hasPendingRef.current = next.some((m) => m.invite_status === 'pending');
    } catch {
      setMembers([]);
      hasPendingRef.current = false;
    }
  }, []);

  const hasPendingRef = React.useRef(false);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // Polling driven by a ref so this effect doesn't depend on `members`
  // (avoids the React 19 set-state-in-effect cascade warning).
  useEffect(() => {
    const handle = setInterval(() => {
      if (hasPendingRef.current) fetchMembers();
    }, 12000);
    return () => clearInterval(handle);
  }, [fetchMembers]);

  const owners = useMemo(
    () => members.filter((m) => m.is_owner && m.user_id !== currentUserId),
    [members, currentUserId],
  );
  const joined = useMemo(
    () => members.filter(
      (m) => !m.is_owner
        && (!m.invite_status || m.invite_status === 'accepted')
        && m.user_id !== currentUserId,
    ),
    [members, currentUserId],
  );
  const pending = useMemo(
    () => members.filter((m) => m.invite_status === 'pending'),
    [members],
  );

  const handleCardClick = useCallback((member) => {
    if (member.is_owner) return;
    setExpandedId((curr) => (curr === member.id ? null : member.id));
  }, []);

  const handleMessageClick = useCallback(async (member, e) => {
    e.stopPropagation();
    if (!member.user_id) {
      toast.info(`${member.name || 'This member'} hasn't joined yet`);
      return;
    }
    try {
      const res = await axios.post(
        `${API}/messages/open-conversation`,
        { recipient_id: member.user_id },
        { headers: getHeaders() },
      );
      if (res.data?.id) navigate(`/staff-admin/chat/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open chat');
    }
  }, [navigate]);

  const handleCreateInvite = async () => {
    setExpandedId(null);
    try {
      const activeVenueAppId = localStorage.getItem('activeVenueAppId');
      const res = await axios.post(
        `${API}/venue/staff-invites`,
        { venue_app_id: activeVenueAppId || undefined },
        { headers: getHeaders() },
      );
      setMembers((prev) => [...prev, res.data]);
      // Branded allurexp.com URL only — matches the App-Link intent filter.
      const inviteUrl = buildInviteUrl(res.data.invite_token);
      // Airbridge: invite created. `inviter_role: 'venue_admin'` separates this
      // funnel from the owner-driven staff-invite funnel (which uses
      // `venue_owner`) so the Airbridge dashboard can split conversion.
      trackInviteCreated({
        inviteId: res.data.invite_token,
        inviteType: 'venue_staff',
        source: 'manual',
        extra: {
          inviter_role: 'venue_admin',
          venue_app_id: activeVenueAppId || '',
        },
      });
      const result = await shareOrCopy({
        title: 'Join us on Allure',
        text: `You're invited to join our team — tap to set up your access.`,
        url: inviteUrl,
      });
      if (result.method === 'share' && !result.cancelled) toast.success('Invite shared');
      else if (result.method === 'share' && result.cancelled) toast.info('Invite ready — tap the card to share again');
      else if (result.method === 'copy') toast.success('Invite link copied — share it with your teammate');
      else toast.success('Invite ready — copy the link from the card to share');
      if (result.method === 'share' && !result.cancelled) {
        trackInviteShared({
          inviteId: res.data.invite_token,
          inviteType: 'venue_staff',
          channel: 'share_sheet',
          extra: { inviter_role: 'venue_admin' },
        });
      } else if (result.method === 'copy') {
        trackInviteShared({
          inviteId: res.data.invite_token,
          inviteType: 'venue_staff',
          channel: 'clipboard',
          extra: { inviter_role: 'venue_admin' },
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create invite');
    }
  };

  const handleCancelInvite = async (memberId) => {
    try {
      await axios.delete(`${API}/venue/staff-invites/${memberId}`, { headers: getHeaders() });
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      toast.success('Invite cancelled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel invite');
    }
  };

  const extractTokenFromInviteUrl = (url) => {
    const m = /\/staff-invite\/([^/?#]+)/.exec(url || '');
    return m ? m[1] : '';
  };

  const handleShareInviteUrl = async (rawUrl) => {
    const inviteToken = extractTokenFromInviteUrl(rawUrl);
    const url = rawUrl;
    const result = await shareOrCopy({
      title: 'Join us on Allure',
      text: `You're invited to join our team — tap to set up your access.`,
      url,
    });
    if (result.method === 'share' && !result.cancelled) toast.success('Invite shared');
    else if (result.method === 'copy') toast.success('Invite link copied');
    else if (result.method === 'failed') toast.error('Failed to share or copy');
    if (inviteToken && result.method === 'share' && !result.cancelled) {
      trackInviteShared({
        inviteId: inviteToken,
        inviteType: 'venue_staff',
        channel: 'share_sheet',
        extra: { inviter_role: 'venue_admin', reshare: true },
      });
    } else if (inviteToken && result.method === 'copy') {
      trackInviteShared({
        inviteId: inviteToken,
        inviteType: 'venue_staff',
        channel: 'clipboard',
        extra: { inviter_role: 'venue_admin', reshare: true },
      });
    }
  };

  const handleCopyInvite = async (rawUrl) => {
    const inviteToken = extractTokenFromInviteUrl(rawUrl);
    const ok = await copyToClipboard(rawUrl);
    if (ok) toast.success('Invite link copied');
    else toast.error('Unable to copy invite');
    if (ok && inviteToken) {
      trackInviteShared({
        inviteId: inviteToken,
        inviteType: 'venue_staff',
        channel: 'clipboard',
        extra: { inviter_role: 'venue_admin', reshare: true },
      });
    }
  };

  const handleDelete = async (memberId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/venue/staff/${memberId}`, { headers: getHeaders() });
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      toast.success('Member removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove member');
    }
  };

  // Admin staff CANNOT promote others to admin (owner-only privilege).
  const handleToggleAdmin = useCallback((_id, e) => {
    e.stopPropagation();
    toast.info('Only the venue owner can promote staff to admin.');
  }, []);

  // Patch the member card so the admin star toggle is hidden for admins.
  const memberForCard = useCallback((m) => ({
    ...m,
    _canTogglePermissions: false, // hint to suppress admin star button
  }), []);

  const totalShown = owners.length + joined.length + pending.length;

  return (
    <div className="app-shell premium-page" data-testid="admin-members-page" style={{
      minHeight: '100dvh', backgroundColor: 'var(--app-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      <StaffVenueHeader roleLabel="Admin staff" testIdPrefix="admin-members" />

      <div style={{ width: '100%', maxWidth: 360, padding: '28px 16px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <InfoPopoverButton
          testId="admin-members-page-info"
          size="sm"
          align="start"
          title="Team members"
          tip="As an admin you can invite new teammates, remove members, and promote / demote others. Tap a member to manage them."
        />
        <h2 style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Members</h2>
      </div>

      <div className="premium-card" style={{
        width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 12,
        borderRadius: 22, padding: '12px 10px 16px',
        maxHeight: 460, overflowY: 'auto',
      }}>
        <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, color: 'var(--text)', textAlign: 'right', marginBottom: 8, paddingRight: 4 }} data-testid="admin-member-count">
          {`${totalShown} Member${totalShown === 1 ? '' : 's'}`}
        </p>

        {joined.length === 0 && pending.length === 0 ? (
          <VenueEmptyState
            icon={UserPlus}
            title="Invite your first teammate"
            description="Send a one-tap invite link — they'll join after creating their Allure account."
            actionLabel="Invite staff member"
            onAction={handleCreateInvite}
            testId="admin-staff-empty-state"
            compact
          />
        ) : null}

        {[...owners, ...joined].map((member) => (
          <VenueStaffMemberCard
            key={member.id}
            handleDelete={handleDelete}
            handleMessageClick={handleMessageClick}
            handleToggleAdmin={handleToggleAdmin}
            isExpanded={expandedId === member.id && !member.is_owner}
            member={memberForCard(member)}
            onCardClick={handleCardClick}
          />
        ))}

        {pending.map((member) => (
          <VenueStaffPendingInviteCard
            key={member.id}
            member={member}
            inviteUrl={buildInviteUrl(member.invite_token)}
            onCopy={handleCopyInvite}
            onShare={handleShareInviteUrl}
            onCancel={handleCancelInvite}
          />
        ))}
      </div>

      {/* Spacer pushes invite button to bottom of viewport */}
      <div style={{ flex: 1 }} />

      <button onClick={handleCreateInvite} data-testid="admin-invite-btn" className="premium-pill-button" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        margin: '16px 0 calc(100px + env(safe-area-inset-bottom, 0px))',
        padding: '10px 24px',
        fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, cursor: 'pointer',
      }}>
        <UserPlus size={22} color="var(--text)" strokeWidth={1.5} />
        Invite staff member
      </button>

      <AdminBottomNav />
    </div>
  );
}
